import type { AppDispatch } from "app/store";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { fetchDrawingData } from "./fetchDrawingData";
import { setDrawingData, setDrawingPreview } from "./minimapSlice";
import {
    downloadTiledDrawingData,
    parseTiledManifestUrl,
    resolveTiledManifest,
    type TilingRequest,
} from "./tiledDrawing";
import type { Uuid } from "./types";

/**
 * Download a drawing, choosing between the two routes (BM-13597):
 *
 *   1. **Tiled** — only when the host flag is on (`tiling` present) AND the
 *      drawing's tile set resolves; fetches the ~1 KB manifest, pixels
 *      stream on demand.
 *   2. **PDF (legacy)** — pdf.js rasterization; the default, and the runtime
 *      fallback when a tiled attempt fails and the drawing has a PDF URL.
 *      With the flag off this is the only route, byte-for-byte the old
 *      behavior.
 *
 * `tiling.waitForMobileProcessing` (mobile only) short-circuits before either
 * route runs: the states stay `Loading` rather than falling back to PDF,
 * since a PDF fallback on mobile is exactly the pdf.js memory/crash risk
 * tiling exists to avoid. The next full refetch (e.g. a page reload)
 * rechecks Model Manager's import status and tries again from `Initial`.
 *
 * PDF route notes: `fetchDrawingData` delivers a fast low-res preview
 * (`previewAsyncState`, drives the mini maps' UI) then slow full-res tiles
 * (`asyncState`, large-map only). Both go `Loading` up front; on failure the
 * tiles state goes `Error` but an already-delivered preview stays usable.
 * Intentionally no `AbortController`: an aborted fetch would strand the
 * states between `Loading` and a terminal status (see `useMiniMapData`).
 */
export async function downloadDrawingData({
    dispatch,
    drawingId,
    url,
    telemetrySource,
    tiling,
}: {
    dispatch: AppDispatch;
    drawingId: Uuid;
    url: string;
    telemetrySource: string;
    /** Tiled-route inputs (`buildTilingRequest`); `undefined` = pure PDF behavior. */
    tiling?: TilingRequest;
}): Promise<void> {
    dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Loading } }));
    dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Loading } }));

    if (tiling?.waitForMobileProcessing) {
        return;
    }

    if (tiling) {
        // Nothing in the tiled attempt may strand the states at `Loading`:
        // no call site attaches a `.catch`, so a throw anywhere in here is
        // treated exactly like a returned `tiledError` (fall through to PDF).
        let tiledError: Error | null = null;
        try {
            // `null` -> no tile set -> PDF route (see tiledDrawing/manifestResolver.ts).
            const manifest = await resolveTiledManifest(url, tiling);
            if (!manifest) {
                tiledError = null;
            } else {
                tiledError = await downloadTiledDrawingData({ dispatch, drawingId, url, manifest, telemetrySource });
                if (!tiledError) {
                    return;
                }
            }
        } catch (error) {
            tiledError = error instanceof Error ? error : new Error(String(error));
            telemetry.trackCaughtException({
                source: telemetrySource,
                category: "render",
                error,
                severity: "warning",
                extraAttributes: { drawingId },
            });
        }
        // Fall back to PDF unless the drawing URL itself is the manifest
        // (dev mock) — then there is nothing to fall back to.
        if (tiledError && parseTiledManifestUrl(url)) {
            dispatch(
                setDrawingPreview({
                    drawingId,
                    asyncState: { status: AsyncStatus.Error, msg: tiledError.message },
                }),
            );
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Error, msg: tiledError.message } }));
            return;
        }
    }

    let previewDelivered = false;

    return fetchDrawingData(url, {
        onPreview: (preview) => {
            previewDelivered = true;
            dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Success, data: preview } }));
        },
    })
        .then((image) => {
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Success, data: image } }));
        })
        .catch((error) => {
            const e = error as Error;
            telemetry.trackCaughtException({
                source: telemetrySource,
                category: "render",
                error,
                severity: "error",
                extraAttributes: { drawingId },
            });
            dispatch(setDrawingData({ drawingId, asyncState: { status: AsyncStatus.Error, msg: e.message } }));
            // Only fail the preview if it never arrived; a delivered preview stays
            // usable even when the background tiling fails.
            if (!previewDelivered) {
                dispatch(setDrawingPreview({ drawingId, asyncState: { status: AsyncStatus.Error, msg: e.message } }));
            }
        });
}
